import { css } from 'aphrodite';
import * as React from 'react';
import { AIConversationType } from '../../../../../../extViewmodels/sdk/models/AI';
import { useAIAgentTemplatesQuery } from '../../../../../../queries/AIAgentTemplate/useAIAgentTemplatesQuery';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../web/components/Select';
import { bs } from '../../../../../../web/styles/styles';

interface CoachingTemplateSelectorProps {
	value: string | null;
	onValueChange: (value: string | null) => void;
	/**
	 * Optional array of coaching types to filter templates by.
	 * If not provided, defaults to all coaching-related conversation types.
	 */
	coachingTypes?: AIConversationType[];
}

/**
 * CoachingTemplateSelector - Dropdown for selecting AI Agent Templates for call coaching
 * Filters for active templates with coaching-related conversation types
 */
export const CoachingTemplateSelector: React.FC<CoachingTemplateSelectorProps> = ({
	value,
	onValueChange,
	coachingTypes: coachingTypesProp,
}) => {
	const id = React.useId();
	const templatesQuery = useAIAgentTemplatesQuery();

	// Default coaching-related conversation types (used if no coachingTypes prop is provided)
	const defaultCoachingTypes = React.useMemo(
		() => [
			AIConversationType.SalesLiveCoaching,
			AIConversationType.SalesPostCallCoaching,
			AIConversationType.SalesPreCallCoaching,
			AIConversationType.SalesDemoCallCoaching,
			AIConversationType.SalesPracticeCall,
		],
		[]
	);

	// Use provided coaching types or default to all coaching types
	const coachingTypes = coachingTypesProp ?? defaultCoachingTypes;

	// Filter templates for coaching templates (both active and inactive)
	const filteredTemplates = React.useMemo(() => {
		if (!templatesQuery.data) return [];
		return templatesQuery.data.filter(template => coachingTypes.includes(template.conversationType));
	}, [templatesQuery.data, coachingTypes]);

	// Handle template selection
	const handleValueChange = (newValue: string) => {
		if (newValue === 'default') {
			onValueChange(null);
		} else {
			onValueChange(newValue);
		}
	};

	// Get display value for the select
	const displayValue = value || 'default';

	// Get display label for selected value
	const getDisplayLabel = () => {
		if (!value) return 'Default';
		const selectedTemplate = filteredTemplates.find(t => t.id === value);
		return selectedTemplate?.name || 'Default';
	};

	// Show loading state
	if (templatesQuery.isLoading) {
		return <div className={css(bs.textXs, bs.textGray600)}>Loading templates...</div>;
	}

	// Show error state
	if (templatesQuery.isError) {
		return <div className={css(bs.textXs, bs.textGray600)}>Failed to load templates</div>;
	}

	// Show no templates message
	if (filteredTemplates.length === 0) {
		return <div className={css(bs.textXs, bs.textGray600)}>No coaching templates available</div>;
	}

	return (
		<div className={css(bs.flex, bs.itemsCenter)} style={{ gap: '8px' }}>
			<label htmlFor={`${id}-template-select`} className={css(bs.textXs, bs.fontBold)} style={{ whiteSpace: 'nowrap' }}>
				AI Agent Template
			</label>
			<Select value={displayValue} onValueChange={handleValueChange}>
				<SelectTrigger id={`${id}-template-select`} styles={[bs.wFull]} style={{ minWidth: '200px' }}>
					<SelectValue
						render={() => (
							<span className={css(bs.textSm)} data-slot='select-value'>
								{getDisplayLabel()}
							</span>
						)}
					/>
				</SelectTrigger>
				<SelectContent>
					<SelectItem value='default'>Default</SelectItem>
					{filteredTemplates.map(template => (
						<SelectItem key={template.id} value={template.id}>
							{template.name}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	);
};
